import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { api, getErrorMessage } from '@/utils';
import { API_ENDPOINTS, QUERY_CONFIG } from '@/constants';

import type { CampaignQuestion } from '../types';

export type QuestionStorage = 'aiInsights' | 'leadApp';

interface CampaignQuestionsData {
    questions: CampaignQuestion[];
    storage: QuestionStorage;
}

interface UseFetchCampaignQuestionsProps {
    data: CampaignQuestion[];
    storage: QuestionStorage;
    isLoading: boolean;
    isError: boolean;
    errorMessage: string | null;
}

/**
 * Fetches campaign questions for a given campaign.
 * Returns both the questions array and the storage location ('aiInsights' | 'leadApp').
 * Use storage to decide whether to show a migration button.
 */
const useFetchCampaignQuestions = (campaignId: number | null): UseFetchCampaignQuestionsProps => {
    const { data, isLoading, isError, error } = useQuery<CampaignQuestionsData, AxiosError>({
        queryKey: ['api', 'campaign', campaignId, 'questions'],
        queryFn: async () => {
            if (!campaignId) {
                return { questions: [], storage: 'aiInsights' as const };
            }

            const response = await api.get(API_ENDPOINTS.CAMPAIGN_QUESTIONS(campaignId));

            return response.data as CampaignQuestionsData;
        },
        staleTime: QUERY_CONFIG.STALE_TIME,
        refetchOnWindowFocus: QUERY_CONFIG.REFETCH_ON_WINDOW_FOCUS,
        retry: 2,
    });

    const errorMessage = getErrorMessage(error);

    return {
        data: data?.questions ?? [],
        storage: data?.storage ?? 'aiInsights',
        isLoading,
        isError,
        errorMessage,
    };
};

export default useFetchCampaignQuestions;
